import {
  Card,
  Container,
  DialogCloseButton,
  Divider,
  Heading,
  Spacer,
  Switch,
  Thumbnail,
  useToggle,
} from '@sparemin/blockhead';
import { useGetEpisodeArt } from 'api/hooks/useGetPodcastEpisodeDetail';
import CopyUrlDisplay from 'components/CopyUrlDisplay';
import { notifySuccess } from 'components/notification';
import { useBreakpoints } from 'context/BreakpointsContext';
import { useEventTracking } from 'context/EventTrackingContext';
import { useModalContents } from 'context/ModalContentsContext';
import { ShareModal, useModalControls } from 'context/ModalContext';
import { useCallback } from 'react';
import { copyToClipboard } from 'utils/clipboard';
import { prepareShareUrl } from 'utils/share';
import { formatDuration } from 'utils/time';
import styles from './ShareModalContents.module.scss';

interface ShareModalContentsProps {}

const ShareModalContents: React.FC<ShareModalContentsProps> = () => {
  const { params } = useModalContents<ShareModal['params']>();

  const { podcastId, episodeId, timestamp = 0 } = params;

  const { data: source } = useGetEpisodeArt({ podcastId, episodeId });

  const { close } = useModalControls();
  const { trackShareEpisode } = useEventTracking();
  const { md } = useBreakpoints();

  const { value: hasTimestamp, toggle } = useToggle(false);
  const url = window.location.href;

  const handleClick = useCallback(() => {
    const urlTimestamp = hasTimestamp ? timestamp : undefined;
    const shareUrl = prepareShareUrl(url, urlTimestamp);

    copyToClipboard(shareUrl);
    notifySuccess({ heading: 'Episode link copied to clipboard!' });
    trackShareEpisode();
    close();
  }, [hasTimestamp, timestamp, trackShareEpisode, close, url]);

  return (
    <Card
      header={
        <Card.Header
          title={null}
          actionButton={<DialogCloseButton onPress={() => close()} />}
        />
      }
      fullScreen={md}
    >
      <Card.Contents
        as={Spacer}
        space="30px"
        orientation="vertical"
        align="center"
      >
        <Thumbnail
          src={source}
          alt="episode art"
          ratio={1}
          className={styles.artwork}
        />
        <Heading level={1} className={styles.text}>
          Share this episode
        </Heading>
        <Container
          as={Spacer}
          space={3}
          borderWeight="regular"
          orientation="vertical"
          className={styles.contents}
        >
          <CopyUrlDisplay url={url} onPress={handleClick} />

          <Divider className={styles.divider} />

          <Switch
            className={styles.switch}
            isMobile={md}
            onChange={(val: boolean) => toggle(val)}
            isSelected={hasTimestamp}
            labelPosition="before"
          >
            <Heading
              className={styles.text}
              level={2}
            >{`Start at ${formatDuration(timestamp, 'zero')}`}</Heading>
          </Switch>
        </Container>
      </Card.Contents>
    </Card>
  );
};
export default ShareModalContents;
